'use client';

import React, { useState, useRef, useEffect } from 'react';
import { trackEvent } from '@services/tracking';
import sendGoogleTagEvent from '@hooks/useSendGoogleTagEvent';
import { useAppSelector } from '@store/index';
import { useSwrEndInterview } from '@hooks/useSwrApi/endpoints/interview/useSwrEndInterview';
import { TailwindSpinner } from '@components/common/Elements/Spinner';
import { useTranslation } from 'next-i18next';
import { useAudioPermission } from './hooks/useAudioPermission';
import { InterviewLandingPage } from './partials/InterviewLandingPage';
import AudioInterviewRoom from './AudioInterviewRoom';
import { AudioInterviewProps, ConnectionState } from './types';

/**
 * AudioInterview
 *
 * The AudioInterview component is the main orchestrator for the audio interview experience.
 * It coordinates the overall interview flow by:
 * - Managing and displaying permission prompts for microphone access
 * - Handling network/API requests to start an interview session
 * - Displaying appropriate UI for different interview states (landing, connecting, in room)
 * - Integrating hooks to handle user session, audio permissions, and interview session data
 * - Passing session data to the AudioInterviewRoom sub-component for the actual interview call experience
 *
 * Props:
 * - locale: string (optional) — provides localization/language context for the interview flow
 * - slug: string (optional) — job slug for job-based interviews
 * - scope: 'cv' | 'job' (optional, default: 'cv') — determines the interview scope. When 'job', the interview will not start without a slug present
 */
const AudioInterview: React.FC<AudioInterviewProps> = ({
  locale = 'ro',
  slug,
  scope = 'cv',
  onComplete,
  skipLanding = false,
}) => {
  const { t } = useTranslation('common');
  const userResume = useAppSelector((state) => state.userOwnResume.userResume);

  // Custom hook for audio permission
  const {
    audioPermission, audioStream, requestPermission,
  } = useAudioPermission();

  const { trigger: endInterview } = useSwrEndInterview({
    locale,
    slug: scope === 'job' ? slug : userResume?.slug,
  });

  // Simple state management for interview session
  const [connectionState, setConnectionState] = useState<ConnectionState>('idle');
  // Track if we've intentionally disconnected to prevent auto-refetch
  const hasDisconnectedRef = useRef(false);
  // Track if we've already requested permission in skipLanding mode
  const hasRequestedPermissionRef = useRef(false);

  const startInterview = () => {
    hasDisconnectedRef.current = false;
    setConnectionState('connecting');
  };

  const disconnect = async () => {
    // Mark as disconnected to prevent any automatic re-entry
    hasDisconnectedRef.current = true;

    void endInterview();

    // track event
    if (slug) {
      // track event for job interview
      trackEvent({
        eventType: 'action',
        entityType: 'job',
        entityId: slug,
        action: 'call',
      });
    } else {
      // track event for resume interview
      trackEvent({
        eventType: 'action',
        entityType: 'resume',
        entityId: userResume?.slug ?? '',
        action: 'call',
      });
    }

    sendGoogleTagEvent('interview_answer', 'Custom');

    // Notify parent that interview is complete
    if (onComplete) {
      onComplete();
    }

    // Reset to idle immediately to prevent re-entry
    setConnectionState('idle');
  };

  // Handle start interview
  const handleStartInterview = async () => {
    if (audioPermission !== 'granted') {
      return;
    }

    // For job scope, ensure slug is present before starting
    if (scope === 'job' && !slug) {
      return;
    }

    // Ensure we have a stream when permission is already granted passively
    let stream = audioStream;
    if (!stream) {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch (e) {
        return;
      }
    }

    // Start the interview - this will change state to 'connecting'
    startInterview();
  };

  // Auto-start flow when skipLanding is true
  useEffect(() => {
    if (!skipLanding || hasDisconnectedRef.current || connectionState !== 'idle') return undefined;

    // Request permission automatically if not already granted and not already requested
    if ((audioPermission === 'idle' || audioPermission === 'denied') && !hasRequestedPermissionRef.current) {
      hasRequestedPermissionRef.current = true;
      void requestPermission();
      return undefined;
    }

    // If permission is granted, start the interview
    if (audioPermission === 'granted' && connectionState === 'idle') {
      // For job scope, ensure slug is present before starting
      if (scope === 'job' && !slug) {
        return undefined;
      }

      // Ensure we have a stream
      if (!audioStream) {
        navigator.mediaDevices.getUserMedia({ audio: true })
          .then(() => {
            startInterview();
          })
          .catch(() => {
            // Permission error, will be handled by permission state
          });
        return undefined;
      }

      startInterview();
    }

    return undefined;
  }, [skipLanding, audioPermission, audioStream, connectionState, scope, slug, requestPermission]);

  // If user clicked start interview, show the interview room
  // Only show if we haven't intentionally disconnected
  if ((connectionState === 'connecting' || connectionState === 'connected') && !hasDisconnectedRef.current) {
    return (
      <AudioInterviewRoom
        locale={locale}
        slug={slug}
        onDisconnect={() => { void disconnect(); }}
      />
    );
  }

  // Skip landing page mode - show simple loading/permission state
  if (skipLanding) {
    return (
      <div className="flex min-h-[400px] flex-col items-center justify-center space-y-4">
        {(audioPermission === 'requesting' || audioPermission === 'idle' || (audioPermission === 'granted' && connectionState === 'idle')) && (
          <>
            <TailwindSpinner className="size-8" />
            {audioPermission === 'requesting' && (
              <p className="text-center text-sm text-ink-medium">
                {t('audio-interview.microphone-permission.requesting')}
              </p>
            )}
          </>
        )}
        {audioPermission === 'denied' && (
          <>
            <TailwindSpinner className="size-8" />
            <p className="text-center text-sm text-ink-medium">
              {t('audio-interview.microphone-permission.denied.description')}
            </p>
          </>
        )}
      </div>
    );
  }

  // Default state - Landing page
  return (
    <InterviewLandingPage
      permissionState={audioPermission}
      onRequestPermissionAction={() => { void requestPermission(); }}
      onStartInterviewAction={() => { void handleStartInterview(); }}
    />
  );
};

export default AudioInterview;

